import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useHomepageContent } from '../../hooks/useHomepageContent'
import AdminBreadcrumb from './AdminBreadcrumb'
import SafeIcon from '../../common/SafeIcon'
import LoadingSpinner from '../ui/LoadingSpinner'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'

const { FiSave, FiEye, FiHome, FiPlus, FiTrash2 } = FiIcons

const HomepageContentManager = () => {
  const { content, loading, updateContent } = useHomepageContent()
  const [activeSection, setActiveSection] = useState('hero')
  const [editingContent, setEditingContent] = useState({})
  const [saving, setSaving] = useState(false)

  React.useEffect(() => {
    if (content[activeSection]) {
      setEditingContent(content[activeSection])
    }
  }, [content, activeSection])

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin' },
    { label: 'Homepage Content', href: '#' }
  ]

  const sections = [
    { id: 'hero', label: 'Hero Section', icon: FiHome },
    { id: 'howItWorks', label: 'How It Works', icon: FiEye },
    { id: 'testimonials', label: 'Testimonials', icon: FiEye },
    { id: 'newsletter', label: 'Newsletter', icon: FiEye },
    { id: 'faq', label: 'FAQ', icon: FiEye },
    { id: 'seo', label: 'SEO Settings', icon: FiEye }
  ]

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateContent(activeSection, editingContent)
      toast.success('Content saved successfully!')
    } catch (error) {
      toast.error('Failed to save content')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (path, value) => {
    const keys = path.split('.')
    const newContent = { ...editingContent }
    let current = newContent

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {}
      current = current[keys[i]]
    }

    current[keys[keys.length - 1]] = value
    setEditingContent(newContent)
  }

  const addArrayItem = (path, defaultItem) => {
    const keys = path.split('.')
    const newContent = { ...editingContent }
    let current = newContent

    for (const key of keys.slice(0, -1)) {
      if (!current[key]) current[key] = {}
      current = current[key]
    }

    const arrayKey = keys[keys.length - 1]
    if (!current[arrayKey]) current[arrayKey] = []
    current[arrayKey] = [...current[arrayKey], defaultItem]
    
    setEditingContent(newContent)
  }

  const removeArrayItem = (path, index) => {
    const keys = path.split('.')
    const newContent = { ...editingContent }
    let current = newContent

    for (const key of keys.slice(0, -1)) {
      current = current[key]
    }

    const arrayKey = keys[keys.length - 1]
    current[arrayKey] = current[arrayKey].filter((_, i) => i !== index)
    
    setEditingContent(newContent)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  const renderHeroEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Headline</label>
        <input
          type="text"
          value={editingContent.headline || ''}
          onChange={(e) => updateField('headline', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subheadline</label>
        <textarea
          value={editingContent.subheadline || ''}
          onChange={(e) => updateField('subheadline', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA Text</label>
          <input
            type="text"
            value={editingContent.primaryCTA?.text || ''}
            onChange={(e) => updateField('primaryCTA.text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA Link</label>
          <input
            type="text"
            value={editingContent.primaryCTA?.link || ''}
            onChange={(e) => updateField('primaryCTA.link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA Text</label>
          <input
            type="text"
            value={editingContent.secondaryCTA?.text || ''}
            onChange={(e) => updateField('secondaryCTA.text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA Link</label>
          <input
            type="text"
            value={editingContent.secondaryCTA?.link || ''}
            onChange={(e) => updateField('secondaryCTA.link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
          />
        </div>
      </div>
    </div>
  )

  const renderTestimonialsEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={editingContent.title || ''}
          onChange={(e) => updateField('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">Testimonials</label>
          <button
            onClick={() => addArrayItem('testimonials', {
              name: '',
              result: '',
              quote: '',
              image: ''
            })}
            className="flex items-center space-x-1 text-nutrition-600 hover:text-nutrition-700"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add Testimonial</span>
          </button>
        </div>

        {(editingContent.testimonials || []).map((testimonial, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Testimonial {index + 1}</h4>
              <button
                onClick={() => removeArrayItem('testimonials', index)}
                className="text-red-600 hover:text-red-700"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Name"
                value={testimonial.name || ''}
                onChange={(e) => updateField(`testimonials.${index}.name`, e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
              />
              
              <input
                type="text"
                placeholder="Result (e.g., Lost 35 lbs in 6 months)"
                value={testimonial.result || ''}
                onChange={(e) => updateField(`testimonials.${index}.result`, e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
              />
            </div>
            
            <textarea
              placeholder="Quote"
              value={testimonial.quote || ''}
              onChange={(e) => updateField(`testimonials.${index}.quote`, e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
            />
            
            <input
              type="url"
              placeholder="Image URL"
              value={testimonial.image || ''}
              onChange={(e) => updateField(`testimonials.${index}.image`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
            />
          </div>
        ))}
      </div>
    </div>
  )

  const renderFAQEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={editingContent.title || ''}
          onChange={(e) => updateField('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">FAQ Items</label>
          <button
            onClick={() => addArrayItem('questions', {
              question: '',
              answer: ''
            })}
            className="flex items-center space-x-1 text-nutrition-600 hover:text-nutrition-700"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add FAQ</span>
          </button>
        </div>

        {(editingContent.questions || []).map((faq, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">FAQ {index + 1}</h4>
              <button
                onClick={() => removeArrayItem('questions', index)}
                className="text-red-600 hover:text-red-700"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            </div>
            
            <input
              type="text"
              placeholder="Question"
              value={faq.question || ''}
              onChange={(e) => updateField(`questions.${index}.question`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
            />
            
            <textarea
              placeholder="Answer"
              value={faq.answer || ''}
              onChange={(e) => updateField(`questions.${index}.answer`, e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
            />
          </div>
        ))}
      </div>
    </div>
  )

  const renderEditor = () => {
    switch (activeSection) {
      case 'hero':
        return renderHeroEditor()
      case 'testimonials':
        return renderTestimonialsEditor()
      case 'faq':
        return renderFAQEditor()
      default:
        return (
          <div className="text-center py-12 text-gray-500">
            <p>Content editor for {activeSection} coming soon...</p>
            <p className="text-sm mt-2">For now, you can edit the content directly in the database.</p>
          </div>
        )
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminBreadcrumb items={breadcrumbItems} />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Homepage Content</h1>
          <p className="text-gray-600 mt-2">
            Manage your homepage sections and content
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Section Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center space-x-3 ${
                    activeSection === section.id
                      ? 'bg-nutrition-100 text-nutrition-700 border border-nutrition-200'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <SafeIcon icon={section.icon} className="w-4 h-4" />
                  <span>{section.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Editor */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {sections.find(s => s.id === activeSection)?.label}
                </h2>
                
                <div className="flex items-center space-x-3">
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4" />
                    <span>Preview</span>
                  </a>
                  
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {saving ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                    )}
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>

              {renderEditor()}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomepageContentManager